"use client";
import useTranslate from "@app/_hooks/useTranslate";

import { useMarket } from "@app/_context";
import { Input } from "@components/inputs";
import Link from "@components/Link";
import { Button } from "@components/ngg";
import { Heading } from "@components/ui/Heading/Heading";
import { Paragraph } from "@components/ui/Paragraph/Paragraph";
import { useAuth } from "@context/authContext";
import { useSiteInfo } from "@context/siteInfoContext";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { FormEvent } from "react";
import { useState } from "react";
import type { AuthState } from "types";

import { cn } from "@/lib/utils";

export default function ResetView() {
  const t = useTranslate();
  const [sent, setSent] = useState(false);
  const { locale } = useMarket().state.market;
  const queryClient = useQueryClient();
  const { auth, resetPassword } = useAuth();
  const { myPagesRegisterPage } = useSiteInfo();

  const { mutate } = useMutation({
    mutationKey: ["auth"],
    mutationFn: (value: Partial<AuthState>) => {
      queryClient.setQueryData(["auth"], (old: AuthState) => ({
        ...old,
        ...value,
      }));
      return Promise.resolve();
    },
  });

  const handleReset = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);
    const email = formData.get("email")?.toString();
    if (!email) return mutate({ error: "Missing email" });
    await resetPassword(email);
    setSent(true);
  };

  return (
    <>
      <Heading size={5} className={cn(" ")}>
        {t("forgotPassword")}
      </Heading>
      {sent ? (
        <>
          <Paragraph className="my-4 text-base">
            {t("passwordResetSuccess")}
          </Paragraph>
          <button
            type="button"
            onClick={() => mutate({ reset: false, login: true })}
            className="mt-4 text-base text-grey-300 underline hover:opacity-70"
          >
            {t("backToLogin")}
          </button>
        </>
      ) : (
        <>
          <Paragraph className="my-4 text-base">
            {t("passwordResetInstructions")}
          </Paragraph>
          <form className="mt-6 space-y-4" onSubmit={handleReset}>
            <Input
              classNames={{ wrapper: "w-full" }}
              name="email"
              id="email"
              type="email"
              label={`${t("formEmailLabel")}*`}
            />

            <Button
              isLoading={auth?.loading}
              type="submit"
              className="btn-primary"
              fullWidth
            >
              {t("send")}
            </Button>

            <button
              type="button"
              onClick={() => mutate({ reset: false, login: true })}
              className="mt-4 text-base text-grey-300 underline hover:opacity-70"
            >
              {t("backToLogin")}
            </button>

            <hr className="w-full border-grey-150" />

            <p className="text-base text-grey-300">
              {t("noAccount")}{" "}
              {myPagesRegisterPage?.slug ? (
                <Link
                  locale={locale}
                  className="text-black underline"
                  href={myPagesRegisterPage.slug}
                  onClick={() => mutate({ reset: false, login: false })}
                >
                  {t("register")}
                </Link>
              ) : null}
            </p>
          </form>
        </>
      )}
    </>
  );
}
